import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the MapRegions module
 */
export default class MapRegions extends MessageBus {
  /**
   * Constructor function of the MapRegions class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;
    this.animateTime = 200;
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers, and enable interactions.
   * @returns {object} MapRegions A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   * Find all necessary DOM elements used in the view and cache them.
   * @returns {object} MapRegions A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.countries = this.element.querySelector(
      `.${CLASS_NAMES.MAP_REGION_COL_COUNTRIES}`
    );
    this.continentTitles = this.element.querySelectorAll(
      `.${CLASS_NAMES.MAP_REGION_COL_TITLE}`
    );
    return this;
  }

  /**
   * Enable event listeners.
   * @returns {object} MapRegions A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.continentTitles.forEach((title) => {
      title.addEventListener(EVENTS.CLICK, this.handleTitleClick);
    });
    window.addEventListener(EVENTS.RESIZE, this.handleResize);
    return this;
  }

  /**
   * Set up event handlers.
   * @returns {object} MapRegions A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleTitleClick = this.handleTitleClick.bind(this);
    this.handleResize = this.handleResize.bind(this);
    return this;
  }

  /**
   * Handles click events on continent titles.
   * @returns {object} MapRegions A reference to the current instance of the class
   * @param {Event} event - The click event
   */
  handleTitleClick(event) {
    if (window.innerWidth < 1023) {
      const title = event.currentTarget;
      title.classList.toggle(CLASS_NAMES.MAP_REGION_COL_TITLE_MINUS);
      const countriesList = title.nextElementSibling;
      countriesList.classList.toggle(
        CLASS_NAMES.MAP_REGION_COL_COUNTRIES_MENU_EXPAND
      );

      if (countriesList.offsetHeight === 0) {
        this.heightAnimate(countriesList, this.animateTime);
      } else {
        countriesList.style.height = '0';
      }
    }
    return this;
  }

  /**
   * Handles window resize events.
   * @returns {object} MapRegions A reference to the current instance of the class
   */
  handleResize() {
    if (window.innerWidth > 1023) {
      this.countries.style.height = '';
    }
    return this;
  }

  /**
   * Animates the height of an element to auto.
   * @param {HTMLElement} element - The element to animate
   * @param {number} time - Duration of animation in milliseconds
   * @returns {object} MapRegions A reference to the current instance of the class
   */
  heightAnimate(element, time) {
    const el = element; // Assign to a local variable to prevent reassignment errors
    const curHeight = el.offsetHeight;
    el.style.height = 'auto';
    const autoHeight = el.offsetHeight;
    el.style.height = `${curHeight}px`;

    setTimeout(() => {
      el.style.transition = `height ${time}ms`;
      el.style.height = `${autoHeight}px`;
    }, 10);

    return this;
  }
}
